// src/oura/newscan/fixtures/store.ts
//
// The fixture server: an in-memory store of drafts that answers every
// /scan-drafts call the way the backend contract says (scanDraftTypes.ts).
// Synchronous and deterministic; the service adapter (scanDraftMock.ts) adds
// the network-like delays.
//
// Rules it enforces, so the switch to live is boring:
//   - every write sends the version it read; an old one is refused `stale`
//   - a write that adds a NEW lint error is refused `lint` and nothing changes;
//     an already-broken draft stays editable
//   - word edits skip locked rows and report them as `skipped`
//   - a reword locks the row and keeps its slot, number and type
//   - undo goes back up to 20 writes, with labels
//   - approve is refused while a blocker or decide is open, while stale, or
//     while a trend break is not acknowledged; start is idempotent
//   - brand mode (v4.5, Online): the server derives `mode` from `buy`; Online
//     needs 1 to 4 markets (us, sg, my, au, uk; order kept, home last); the
//     searches are pulled with the draft and again on `refreshDemand`
//   - first_check (joined journey, JOURNEY-SPEC §2.5): once a signed client's
//     form is answered (read through journey.ts from the onboarding sample
//     store), the first read for that business drafts its First AI check from
//     the pitched 50, the form and the quote; created approved when nothing
//     changed (carried approval). Its start writes the onboarding record's
//     first assessment through the same seam.
//
// Ids are sequence-based (`draft-<site>-<n>`), never random. The clock is only
// used for timestamps, the drafting job's progress and the Search Console
// connect delay.

import type {
    BuyMode,
    CreateScanDraftRequest,
    DecisionAction,
    ScanDraftPitch,
    DecisionChoice,
    QuestionPatch,
    ScanDraft,
    ScanDraftAlternative,
    ScanDraftBusiness,
    ScanDraftDetected,
    ScanDraftEffect,
    ScanDraftFailure,
    ScanDraftListItem,
    ScanDraftResult,
    ScanDraftWrite,
    StartScanDraftRequest,
    StartScanDraftResult,
    StartedScanDraft,
} from "@service/aeo/scanDraftTypes";
import { CLIENT_SIDE_ACTIONS, scanDraftModeFor } from "@service/aeo/scanDraftTypes";
import {
    assemble,
    BASELINE_MOVE_DAYS,
    brandUnavailable,
    isBrand,
    isStale,
    jobProgress,
    jobSteps,
    rowAlternatives,
    rowValues,
    toScanDraft,
    type Assembly,
    type SearchConnection,
} from "./assemble";
import { MARKET_LABEL, MARKETS_MAX, containsPhrase, marketCode, marketOf, spareById } from "./brand";
import { openGates, plural } from "./decisions";
import { dedupePass } from "./dedupe";
import { errorId, REWORD_MAX, REWORD_MIN } from "./lint";
import { contentOf, UNDO_LIMIT, type DraftContent, type DraftState, type RecipeMode, type RowOverride } from "./model";
import { familyMembers, FORCED_SLOT_KEY, PLACE_VARS, RECIPES, templateById, wordFamily, wordLabel } from "./recipe";
import { domainKey, sampleFor, sampleForBusiness, unknownRead } from "./samples";
import { anchorTown, canonicalTown } from "../towns";
import { activeOutlets, firstCheckBusiness, formServicesOf, OUTLET_CAP, planOutletNames, sameThing, serviceTarget, validPlanKeywords, validPlanOutletNames } from "./firstCheck";
import { coreSwap } from "./formCards";
import { daysAgoIso, journeySample } from "./journeySamples";
import { onboardingSide, SAMPLE_SCAN_MS, type SignedFacts } from "./journey";

/* ------------------------------------------------------------------------- *
 * Clock, store, helpers
 * ------------------------------------------------------------------------- */

const clock = (): number => Date.now();

const STEP_MS_NEW = 1000;
const STEP_MS_REDRAFT = 500;
const GSC_CONNECT_MS = 1200;
const GSC_PULL_MS = 2500;

interface GscState {
    connectedAt: number;
    /** A year of searches is being pulled until then. */
    pullingUntil?: number;
}

const drafts = new Map<string, DraftState>();
const gsc = new Map<string, GscState>();
const sequence = new Map<string, number>();

/** Tests and the dev console: forget every draft and connection. */
export function resetFixtureStore(): void {
    drafts.clear();
    gsc.clear();
    sequence.clear();
}

/** The whole store as plain data, so the dev server can keep it across a page refresh. */
export interface FixtureSnapshot {
    drafts: [string, DraftState][];
    gsc: [string, GscState][];
    sequence: [string, number][];
}

export function snapshotFixtureStore(): FixtureSnapshot {
    return { drafts: Array.from(drafts.entries()), gsc: Array.from(gsc.entries()), sequence: Array.from(sequence.entries()) };
}

export function restoreFixtureStore(snap: FixtureSnapshot): void {
    resetFixtureStore();
    snap.drafts.forEach(([k, v]) => drafts.set(k, v));
    snap.gsc.forEach(([k, v]) => gsc.set(k, v));
    snap.sequence.forEach(([k, v]) => sequence.set(k, v));
}

const ok = <T>(data: T): ScanDraftResult<T> => ({ ok: true, data });
const fail = (kind: ScanDraftFailure["kind"], message: string, extra: Partial<ScanDraftFailure> = {}): ScanDraftFailure => ({
    ok: false,
    kind,
    message,
    ...extra,
});

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;
const slug = (s: string) =>
    s
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "") || "site";

function connection(domain: string): SearchConnection {
    const g = gsc.get(domainKey(domain));
    const connected = Boolean(g && clock() >= g.connectedAt);
    return {
        connected,
        property: connected ? `sc-domain:${domainKey(domain)}` : null,
        connectedAt: connected && g ? g.connectedAt : null,
        pulling: connected && Boolean(g && clock() < (g.pullingUntil ?? 0)),
    };
}

/** Moves a drafting job on to `ready` (or `failed`) once its time is up. */
function advance(state: DraftState): void {
    if (state.status !== "building" || !state.job) return;
    if (!jobProgress(state, clock()).done) return;
    // A brand draft for a site with no pre-authored set fails every time, with its own sentence.
    if (state.job.fail || brandUnavailable(state)) {
        state.status = "failed";
        state.job.fail = false;
    } else {
        state.status = "ready";
    }
    state.version += 1;
    state.updatedAt = state.job.startedAt + state.job.stepMs * jobSteps(state).length;
}

function view(state: DraftState): ScanDraft {
    return toScanDraft(state, clock(), connection(state.domain), { pitchRunning: state.purpose === "first_check" && runningPitch(state) !== null });
}

function isOpen(state: DraftState): boolean {
    return state.status === "building" || state.status === "ready" || state.status === "approved" || state.status === "failed";
}

function load(id: string): DraftState | ScanDraftFailure {
    const state = drafts.get(id);
    if (!state) return fail("refused", "This draft no longer exists.", { code: "not_found" });
    advance(state);
    return state;
}

/** Load for a write: the version must match and the draft must be editable. */
function loadForWrite(id: string, version: number, allowed: DraftState["status"][] = ["ready"]): DraftState | ScanDraftFailure {
    const state = load(id);
    if ("ok" in state) return state;
    if (state.version !== version) return fail("stale", "Someone else changed this draft. Showing the latest.", { code: "stale_draft" });
    if (!allowed.includes(state.status)) return fail("refused", refusalFor(state), { code: `draft_${state.status}` });
    return state;
}

function refusalFor(state: DraftState): string {
    switch (state.status) {
        case "building":
            return "The draft is still being written. Try again in a moment.";
        case "approved":
            return "This set is approved. Reopen it to change anything.";
        case "started":
            return "This draft already started a scan.";
        case "failed":
            return "The draft could not be written. Draft it again first.";
        default:
            return "This draft is closed.";
    }
}

/** The decision the current write answers, set by answerDecision and setRecipe for the length of the call. */
let answering: string | null = null;

/** Open decision ids on a ready draft. */
function openDecisionIds(state: DraftState): Set<string> {
    if (state.status !== "ready") return new Set();
    return new Set(view(state).decisions.filter((d) => d.state === "open").map((d) => d.id));
}

function lintErrorIds(a: Assembly): Set<string> {
    return new Set(a.issues.filter((i) => i.severity === "error").map(errorId));
}

/**
 * Apply a change and keep it only if it adds no lint error. On success it
 * bumps the version, stores an undo step, counts every decision the write
 * closed (however it was made: an answer, a typed word, a reword) and returns
 * the write with its effect.
 */
function commit(
    state: DraftState,
    label: string,
    change: (s: DraftState) => ScanDraftFailure | null,
    opts: { lintGate?: boolean; undoable?: boolean } = {},
): ScanDraftResult<ScanDraftWrite> {
    const lintGate = opts.lintGate ?? true;
    const before = contentOf(state);
    const beforeA = assemble(state);
    const openBefore = openDecisionIds(state);
    const refused = change(state);
    if (refused) {
        restore(state, before);
        return refused;
    }
    const afterA = assemble(state);
    if (lintGate) {
        const had = lintErrorIds(beforeA);
        const added = afterA.issues.find((i) => i.severity === "error" && !had.has(errorId(i)));
        if (added) {
            restore(state, before);
            return fail("lint", `That change would break a check: ${added.message.replace(/^q(\d+):/, "question $1:")}.`, { code: "lint_failed" });
        }
    }
    if (opts.undoable ?? true) {
        state.undo.push({ label, content: before });
        if (state.undo.length > UNDO_LIMIT) state.undo.shift();
    }
    // A write that rebuilds or closes the draft leaves nothing to compare: only its own answer counts.
    const openAfter = openDecisionIds(state);
    const closed = new Set(state.status === "ready" ? Array.from(openBefore).filter((d) => !openAfter.has(d)) : []);
    if (answering) closed.add(answering);
    state.answeredCount = (state.answeredCount ?? 0) + closed.size;
    state.version += 1;
    state.updatedAt = clock();
    return ok({ draft: view(state), effect: effectOf(label, beforeA, afterA) });
}

function restore(state: DraftState, content: DraftContent): void {
    // A field the earlier content did not have goes too (undo past the first "For the plan" answer).
    if (!content.forPlan) delete state.forPlan;
    Object.assign(state, clone(content));
}

/** Which questions changed wording, and which locked ones would have. */
function effectOf(label: string, before: Assembly, after: Assembly): ScanDraftEffect {
    const prior = new Map(before.rows.map((r) => [r.plan.key, r]));
    const changed: number[] = [];
    const skipped: number[] = [];
    after.rows.forEach((r) => {
        const was = prior.get(r.plan.key);
        if (!was) return;
        if (was.question.text !== r.question.text) changed.push(r.question.n);
        else if (r.question.locked && was.fresh !== r.fresh) skipped.push(r.question.n);
    });
    return { label, changed, skipped };
}

function rowAt(a: Assembly, n: number) {
    return a.rows.find((r) => r.question.n === n) ?? null;
}

function frozenOf(a: Assembly, key: string): RowOverride["frozen"] {
    const r = a.rows.find((x) => x.plan.key === key);
    if (!r) return undefined;
    const q = r.question;
    return { text: q.text, fill: q.fill, fill_vars: [...q.fill_vars], fill_values: { ...q.fill_values } };
}

function startJob(state: DraftState, stepMs: number, failOnce: boolean): void {
    state.status = "building";
    state.job = { seq: (state.job?.seq ?? 0) + 1, startedAt: clock(), stepMs, fail: failOnce };
}

const MARKET_REFUSED = (message: string) => fail("refused", message, { code: "invalid_inputs" });

/**
 * The business as the server keeps it: `mode` derived from `buy` (the server
 * value wins). Online: markets checked (1 to 4 known codes, no repeats, order
 * kept, home last), `market` set to the home market's label for older readers,
 * partner products trimmed. A draft from before v4.5 with only `market` gets
 * that market as its one market.
 */
function cleanBusiness(b: ScanDraftBusiness): ScanDraftBusiness | ScanDraftFailure {
    const next = clone(b);
    next.mode = scanDraftModeFor(next.buy);
    if (next.buy !== "online") return next;
    let given = next.markets;
    if (given === undefined) {
        const home = next.market ? marketCode(next.market) : null;
        given = home && home !== "global" ? [marketOf(home)] : [];
    }
    const codes: string[] = [];
    for (const m of given) {
        const code = marketCode(m.code ?? "") ?? marketCode(m.label ?? "");
        if (!code || code === "global") return MARKET_REFUSED("That market is not on the list.");
        if (!codes.includes(code)) codes.push(code);
    }
    if (!codes.length) return MARKET_REFUSED("Add at least 1 market.");
    if (codes.length > MARKETS_MAX) return MARKET_REFUSED(`Up to ${MARKETS_MAX} markets.`);
    next.markets = codes.map((c) => marketOf(c as keyof typeof MARKET_LABEL));
    next.market = MARKET_LABEL[codes[codes.length - 1] as keyof typeof MARKET_LABEL];
    next.sellsToBusinesses = next.sellsToBusinesses === true;
    const seen = new Set<string>();
    next.partnerProducts = (next.partnerProducts ?? [])
        .map((x) => x.trim())
        .filter((x) => x && !seen.has(x.toLowerCase()) && Boolean(seen.add(x.toLowerCase())));
    return next;
}

const isFailure = (v: ScanDraftBusiness | ScanDraftFailure): v is ScanDraftFailure => "ok" in v;

/**
 * city_rule.anchor_is_not_a_town: an outlet named after its own town
 * ("Subang Jaya" in Subang Jaya) is refused until staff name the sub-area or
 * say it has none (`noSmallerArea`, with the town set to the wider ring).
 */
function anchorIsTownRefusal(b: ScanDraftBusiness): ScanDraftFailure | null {
    if (b.buy === "online") return null;
    const hit = b.outlets.find((o) => o.name.trim() && (o.source === "site" || o.confirmed === true) && anchorTown(o, b.city));
    if (!hit) return null;
    const area = hit.name.trim();
    return fail("refused", `${area} is a town, not the outlet's area. Name the part of ${area} the outlet is in.`, {
        code: "anchor_is_town",
    });
}

/* ------------------------------------------------------------------------- *
 * Reads
 * ------------------------------------------------------------------------- */

export function readSite(domain: string): ScanDraftDetected {
    const sample = sampleFor(domain);
    return clone(sample ? sample.detected : unknownRead());
}

export function findOpen(where: { domain?: string; businessId?: string }): ScanDraft | null {
    // A signed client whose form is in: its First AI check is drafted on the first read.
    if (where.businessId) ensureFirstCheck(where.businessId);
    const key = where.domain ? domainKey(where.domain) : null;
    const hit = Array.from(drafts.values()).find((d) => {
        advance(d);
        if (!isOpen(d)) return false;
        if (key && d.domain !== key) return false;
        if (where.businessId && d.businessId !== where.businessId) return false;
        return Boolean(key || where.businessId);
    });
    return hit ? view(hit) : null;
}

/** Every open draft, newest first: what GET /scan-drafts?status=open returns. */
export function listOpen(): ScanDraftListItem[] {
    return Array.from(drafts.values())
        .filter((d) => {
            advance(d);
            return isOpen(d);
        })
        .sort((a, b) => b.updatedAt - a.updatedAt)
        .map((d) => ({
            id: d.id,
            kind: d.kind,
            ...(d.purpose ? { purpose: d.purpose } : {}),
            domain: d.domain,
            businessName: d.business.businessName.trim(),
            businessId: d.businessId,
            status: d.status,
            createdAt: new Date(d.createdAt).toISOString(),
            updatedAt: new Date(d.updatedAt).toISOString(),
        }));
}

/** The business's recipe change is settled: a recipe_change draft started its scan, or kept all. */
function recipeSettled(businessId: string): boolean {
    return Array.from(drafts.values()).some(
        (d) => d.kind === "recipe_change" && d.businessId === businessId && (d.status === "started" || d.recipeMode === "keep_all"),
    );
}

/**
 * Whether a recipe update is on offer for a business, and how many of its 50
 * it changes: an open recipe_change draft's count (none once it kept all), else
 * the sample client on an older recipe until its change is settled.
 */
export function recipeOffer(businessId: string): { changed: number } | null {
    const open = findOpen({ businessId });
    if (open) {
        if (open.kind !== "recipe_change" || !open.recipeChange || open.recipeChange.mode === "keep_all") return null;
        return { changed: open.recipeChange.rows.filter((r) => !r.same).length };
    }
    const existing = sampleForBusiness(businessId)?.existing;
    if (!existing || existing.recipe.stamp === RECIPES.visit.stamp || recipeSettled(businessId)) return null;
    return { changed: Object.keys(existing.oldTemplates).length };
}

export function getDraft(id: string): ScanDraftResult<ScanDraft> {
    const state = load(id);
    return "ok" in state ? state : ok(view(state));
}

export function listAlternatives(id: string, n: number): ScanDraftResult<ScanDraftAlternative[]> {
    const state = load(id);
    if ("ok" in state) return state;
    if (state.status === "building" || state.status === "failed") return fail("refused", refusalFor(state));
    const a = assemble(state);
    const row = rowAt(a, n);
    if (!row) return fail("refused", `There is no question ${n}.`);
    return ok(rowAlternatives(state, a, row));
}

/* ------------------------------------------------------------------------- *
 * Create
 * ------------------------------------------------------------------------- */

function newState(fields: Pick<DraftState, "kind" | "domain" | "businessId" | "sourceAssessmentId" | "sampleDomain" | "recipeFrom"> & {
    business: ScanDraftBusiness;
}): DraftState {
    const site = slug(fields.domain);
    const seq = (sequence.get(site) ?? 0) + 1;
    sequence.set(site, seq);
    const now = clock();
    return {
        ...fields,
        id: `draft-${site}-${seq}`,
        status: "ready",
        version: 1,
        business: clone(fields.business),
        built: clone(fields.business),
        round: 0,
        wordEdits: {},
        rows: {},
        answered: {},
        oldKeys: [],
        recipeMode: null,
        job: null,
        answeredCount: 0,
        baseline: null,
        createdAt: now,
        updatedAt: now,
        approvedAt: null,
        startedAssessmentId: null,
        note: null,
        undo: [],
    };
}

export function createDraft(req: CreateScanDraftRequest): ScanDraftResult<{ draft: ScanDraft; reused: boolean }> {
    if (req.purpose === "first_check") return createFirstCheck(req.businessId);
    if (req.kind === "new_scan") {
        const domain = domainKey(req.domain);
        if (!domain) return fail("refused", "Paste the website first.");
        const sample = sampleFor(domain);
        if (sample?.existing) {
            return fail("exists", `This site is already a client: ${sample.existing.business.businessName}.`, {
                code: "business_exists",
                data: { businessId: sample.existing.businessId, businessName: sample.existing.business.businessName },
            });
        }
        const open = findOpen({ domain });
        if (open) return open.kind === "new_scan" ? ok({ draft: open, reused: true }) : fail("exists", "A draft for this site is already open.", { code: "draft_exists", data: { draftId: open.id } });
        const business = cleanBusiness(req.inputs);
        if (isFailure(business)) return business;
        if (business.buy !== "online" && !business.outlets.some((o) => o.name.trim())) {
            return fail("refused", business.buy === "both" ? "Add at least one showroom." : "Add at least one outlet.", { code: "outlets_required" });
        }
        const townAnchor = anchorIsTownRefusal(business);
        if (townAnchor) return townAnchor;
        if (!business.services.some((s) => s.name.trim())) return fail("refused", "Add at least one service.", { code: "services_required" });
        const state = newState({
            kind: "new_scan",
            domain,
            businessId: null,
            sourceAssessmentId: null,
            sampleDomain: sample ? sample.domain : null,
            recipeFrom: RECIPES[business.buy],
            business,
        });
        startJob(state, STEP_MS_NEW, domain.startsWith("fail"));
        if (isBrand(state)) state.demandPulledAt = clock();
        dedupePass(state);
        drafts.set(state.id, state);
        return ok({ draft: view(state), reused: false });
    }

    const sample = sampleForBusiness(req.businessId);
    const existing = sample?.existing;
    if (!sample || !existing) {
        // A joined-journey business: its frozen set is the first check it ran, else the pitch.
        const frozen = journeySetOf(req.businessId);
        if (!frozen) return fail("refused", "This business has no approved question set to edit yet.", { code: "no_baseline" });
        return editFrozen(req.kind, req.businessId, frozen);
    }
    if (req.kind === "recipe_change" && existing.recipe.stamp === RECIPES.visit.stamp) {
        return fail("refused", "This business already uses the newest recipe.", { code: "no_recipe_change" });
    }
    const open = findOpen({ businessId: req.businessId });
    if (open) return open.kind === req.kind ? ok({ draft: open, reused: true }) : fail("exists", "Another draft for this business is open.", { code: "draft_exists", data: { draftId: open.id } });

    const state = newState({
        kind: req.kind,
        domain: sample.domain,
        businessId: existing.businessId,
        sourceAssessmentId: existing.assessmentId,
        sampleDomain: sample.domain,
        recipeFrom: existing.recipe,
        business: existing.business,
    });
    state.oldKeys = Object.keys(existing.oldTemplates);
    // The first AI check's finish when the onboarding record has one; else a sample client
    // measured weeks ago, past the 30 days, so its baseline no longer moves.
    const finished = onboardingSide()?.facts(req.businessId)?.firstCheck.finishedAt;
    state.baselineAt = finished ? new Date(finished).getTime() : clock() - 45 * 86_400_000;
    // The sample clients on a plan run monthly checks (the business's next_auto_scan_at live).
    state.monthlyAt = nextMonthly(state.baselineAt);
    state.baseline = Object.fromEntries(assemble(state).rows.map((r) => [r.plan.key, r.question.text]));
    // A recipe change drafts a preview on the new recipe: the dedupe pass runs on it. An edit_set opens the frozen set as it is.
    if (req.kind === "recipe_change") {
        startJob(state, STEP_MS_REDRAFT, false);
        dedupePass(state);
    }
    drafts.set(state.id, state);
    return ok({ draft: view(state), reused: false });
}

/**
 * The set a joined-journey business asks today (fixtures): its newest started
 * first check, else the pitch it was scanned with. Null: nothing frozen yet.
 */
function journeySetOf(businessId: string): DraftState | null {
    const started = Array.from(drafts.values())
        .filter((d) => d.status === "started" && d.businessId === businessId)
        .sort((a, b) => b.updatedAt - a.updatedAt)[0];
    if (started) return started;
    const meta = journeySample(businessId);
    return meta ? pitchFor(domainKey(meta.domain))?.state ?? null : null;
}

/** An edit_set (or recipe_change) on a frozen set from the joined journey: opens it as it is. */
function editFrozen(kind: "edit_set" | "recipe_change", businessId: string, frozen: DraftState): ScanDraftResult<{ draft: ScanDraft; reused: boolean }> {
    if (kind === "recipe_change" && frozen.recipeFrom.stamp === RECIPES[frozen.built.buy].stamp) {
        return fail("refused", "This business already uses the newest recipe.", { code: "no_recipe_change" });
    }
    const open = findOpen({ businessId });
    if (open) return open.kind === kind ? ok({ draft: open, reused: true }) : fail("exists", "Another draft for this business is open.", { code: "draft_exists", data: { draftId: open.id } });
    const state = newState({
        kind,
        domain: frozen.domain,
        businessId,
        sourceAssessmentId: frozen.startedAssessmentId ?? frozen.sourceAssessmentId,
        sampleDomain: frozen.sampleDomain,
        recipeFrom: frozen.recipeFrom,
        business: frozen.built,
    });
    // The baseline: the first AI check's finish as the onboarding record has it, else when that set started.
    const finished = onboardingSide()?.facts(businessId)?.firstCheck.finishedAt;
    state.baselineAt = finished ? new Date(finished).getTime() : frozen.updatedAt;
    state.wordEdits = clone(frozen.wordEdits);
    state.rows = clone(frozen.rows);
    state.round = frozen.round;
    state.auto = clone(frozen.auto ?? {});
    state.baseline = Object.fromEntries(assemble(state).rows.map((r) => [r.plan.key, r.question.text]));
    if (kind === "recipe_change") {
        startJob(state, STEP_MS_REDRAFT, false);
        dedupePass(state);
    }
    drafts.set(state.id, state);
    return ok({ draft: view(state), reused: false });
}

/* ------------------------------------------------------------------------- *
 * Business and drafting again
 * ------------------------------------------------------------------------- */

export function patchBusiness(id: string, version: number, patch: Partial<ScanDraftBusiness>): ScanDraftResult<ScanDraftWrite> {
    const state = loadForWrite(id, version, ["ready", "failed"]);
    if ("ok" in state) return state;
    return commit(
        state,
        "Business details saved",
        (s) => {
            const next = cleanBusiness({ ...s.business, ...clone(patch) });
            if (isFailure(next)) return next;
            s.business = next;
            // Inputs that do not feed the 50 apply at once (partner products change the counting, not the questions); the rest wait for Draft again.
            s.built = {
                ...s.built,
                businessName: s.business.businessName,
                rivals: s.business.rivals,
                otherNames: s.business.otherNames,
                requestedBy: s.business.requestedBy,
                ...(s.business.partnerProducts !== undefined ? { partnerProducts: [...s.business.partnerProducts] } : {}),
            };
            return null;
        },
        { lintGate: false },
    );
}

export function rebuild(id: string, version: number, opts?: { refreshWords?: boolean; refreshDemand?: boolean }): ScanDraftResult<ScanDraftWrite> {
    const state = loadForWrite(id, version, ["ready", "failed"]);
    if ("ok" in state) return state;
    const townAnchor = anchorIsTownRefusal(state.business);
    if (townAnchor) return townAnchor;
    const refresh = opts?.refreshWords === true;
    // "Pull the searches again": a fresh Google pull for a brand draft; rows staff changed keep their wording.
    const pull = opts?.refreshDemand === true && state.business.buy === "online";
    return commit(
        state,
        pull ? "Pulled the Google searches again" : refresh ? "Drafted the words again" : "Drafted the 50 again",
        (s) => {
            if (pull) {
                s.demandRound = (s.demandRound ?? 0) + 1;
                s.demandPulledAt = clock();
            }
            const unlocked = redraft(s, refresh);
            startJob(s, refresh ? STEP_MS_NEW : STEP_MS_REDRAFT, false);
            if (s.job && unlocked.length) s.job.unlocked = unlocked;
            return null;
        },
        { lintGate: false },
    );
}

/**
 * The no-AI rebuild: the 50 re-rendered from the current business and the
 * stored words (new words first when `refreshWords`), then the dedupe pass.
 * Locked rows keep their wording when their outlet still has questions; the
 * rest are let go. Returns the `n` of each locked row let go, ascending.
 */
function redraft(s: DraftState, refreshWords = false): number[] {
    const before = new Map(assemble(s).rows.map((r) => [r.plan.key, r.question.n]));
    // A brand draft reuses its stored pull unless it is new to brand mode or its markets changed.
    const markets = (b: ScanDraftBusiness) => (b.buy === "online" ? JSON.stringify((b.markets ?? []).map((m) => m.code)) : "");
    if (s.business.buy === "online" && (s.built.buy !== "online" || markets(s.built) !== markets(s.business) || s.demandPulledAt === undefined)) {
        s.demandPulledAt = clock();
    }
    s.built = clone(s.business);
    if (refreshWords) s.round += 1;
    const a = assemble(s);
    const keys = new Set(a.rows.map((r) => r.plan.key));
    const unlocked: number[] = [];
    Object.keys(s.rows).forEach((key) => {
        if (keys.has(key)) return;
        const n = before.get(key);
        if (s.rows[key].locked && n !== undefined) unlocked.push(n);
        delete s.rows[key];
    });
    dedupePass(s);
    return unlocked.sort((x, y) => x - y);
}

/* ------------------------------------------------------------------------- *
 * Questions
 * ------------------------------------------------------------------------- */

const normalise = (text: string) => text.replace(/\s+/g, " ").trim();

function applyReword(s: DraftState, a: Assembly, n: number, text: string): ScanDraftFailure | null {
    const row = rowAt(a, n);
    if (!row) return fail("refused", `There is no question ${n}.`);
    const next = normalise(text);
    const key = row.plan.key;
    const ov = s.rows[key] ?? {};
    const recipeWording = ov.original ?? (row.question.reworded ? null : row.question.text);
    // Typing the recipe wording back on a reworded row is a reset; on an untouched row it changes nothing.
    if (recipeWording !== null && next === recipeWording) return row.question.reworded ? applyReset(s, a, n) : null;
    if (next.length < REWORD_MIN || next.length > REWORD_MAX)
        return fail("lint", `A question needs ${REWORD_MIN} to ${REWORD_MAX} characters.`, { code: "lint_failed" });
    s.rows[key] = { ...ov, text: next, original: ov.original ?? row.question.text, locked: true, frozen: undefined };
    return null;
}

/** Back to the recipe wording, and unlocked: the row follows the word edits again. */
function applyReset(s: DraftState, a: Assembly, n: number): ScanDraftFailure | null {
    const row = rowAt(a, n);
    if (!row) return fail("refused", `There is no question ${n}.`);
    const key = row.plan.key;
    const ov = { ...(s.rows[key] ?? {}) };
    delete ov.text;
    delete ov.original;
    delete ov.locked;
    delete ov.frozen;
    s.rows[key] = ov;
    return null;
}

export function patchQuestion(id: string, version: number, n: number, patch: QuestionPatch): ScanDraftResult<ScanDraftWrite> {
    const state = loadForWrite(id, version);
    if ("ok" in state) return state;
    const a = assemble(state);
    if ("text" in patch) return commit(state, `Question ${n} reworded`, (s) => applyReword(s, a, n, patch.text));
    if ("reset" in patch) {
        const target = rowAt(a, n);
        if (target && !target.question.reworded) return fail("refused", `Question ${n} already uses the recipe wording.`, { code: "not_reworded" });
        return commit(state, `Question ${n} back to the recipe wording`, (s) => applyReset(s, a, n));
    }
    const row = rowAt(a, n);
    if (!row) return fail("refused", `There is no question ${n}.`);
    const key = row.plan.key;
    return commit(
        state,
        patch.locked ? `Question ${n} locked` : `Question ${n} unlocked`,
        (s) => {
            const ov = { ...(s.rows[key] ?? {}) };
            if (patch.locked) {
                ov.locked = true;
                if (ov.text === undefined) ov.frozen = frozenOf(a, key);
            } else {
                ov.locked = false;
                delete ov.frozen;
            }
            s.rows[key] = ov;
            return null;
        },
        { lintGate: false },
    );
}

function applyAlternative(s: DraftState, a: Assembly, n: number, altId: string): ScanDraftFailure | null {
    const row = rowAt(a, n);
    if (!row) return fail("refused", `There is no question ${n}.`);
    if (row.question.locked) return fail("refused", `Question ${n} is locked. Unlock it first.`, { code: "row_locked" });
    const offered = rowAlternatives(s, a, row).find((x) => x.id === altId);
    if (!offered) return fail("refused", "That wording is no longer on offer. Try another again.", { code: "alternative_gone" });
    const key = row.plan.key;
    const ov = { ...(s.rows[key] ?? {}) };
    if (altId.startsWith("d:")) {
        // Brand: another pulled Google search, asked as written.
        if (!spareById(a.sample?.brand, altId)) return fail("refused", "That search is no longer on offer.");
        ov.tpl = altId;
        delete ov.values;
    } else if (altId.startsWith("t:")) {
        if (!templateById(altId.slice(2))) return fail("refused", "That wording is no longer on offer.");
        ov.tpl = altId.slice(2);
        delete ov.values;
    } else {
        const [, variable, ...rest] = altId.split(":");
        // The words the offer was rendered with (the pass's word fallback included), plus the new pick.
        ov.values = { ...rowValues(s, key), [variable]: rest.join(":") };
    }
    s.rows[key] = ov;
    return null;
}

export function acceptAlternative(id: string, version: number, n: number, altId: string): ScanDraftResult<ScanDraftWrite> {
    const state = loadForWrite(id, version);
    if ("ok" in state) return state;
    const a = assemble(state);
    return commit(state, `Question ${n} asked another way`, (s) => applyAlternative(s, a, n, altId));
}

/* ------------------------------------------------------------------------- *
 * Words
 * ------------------------------------------------------------------------- */

function applyWord(s: DraftState, a: Assembly, variable: string, value: string): ScanDraftFailure | null {
    if (PLACE_VARS.has(variable)) return fail("refused", "Set this one on the Business step.", { code: "word_not_editable" });
    const next = normalise(value);
    if (next.length > 80) return fail("refused", "Keep a word under 80 characters.", { code: "value_too_long" });
    if (/[{}]/.test(next)) return fail("refused", "A word cannot contain { or }.", { code: "value_invalid" });
    if (a.lintCtx.nameTokens.some((t) => new RegExp(`\\b${t}\\b`, "i").test(next)))
        return fail("lint", "That word names the business. The questions must not.", { code: "value_names_business" });
    // Brand: the brand's own lines are not buyers' words (the differentiator guard).
    if (a.lintCtx.brand?.claimPhrases.some((p) => containsPhrase(next, p)))
        return fail("lint", "That is the brand's own line. Use the words buyers search.", { code: "value_is_claim" });
    // The same word as another of its family ("root canal" twice) asks the same question twice: say so before it lands.
    const twin = next ? familyMembers(variable).find((v) => a.src.value(v).trim().toLowerCase() === next.toLowerCase()) : undefined;
    if (twin) {
        return fail("lint", `${next.charAt(0).toUpperCase()}${next.slice(1)} is already the ${wordLabel(twin).toLowerCase()}. Type another one.`, { code: "value_repeats_word" });
    }
    // A blank being filled: the rows it completes join the generation-time dedupe, so the fill
    // never leaves two questions word for word the same (a changed word still raises its decision).
    const filling = a.rows.some((r) => r.missing.includes(variable));
    if (next === a.src.pick(variable)) delete s.wordEdits[variable];
    else s.wordEdits[variable] = next;
    if (filling && next) dedupePass(s);
    return null;
}

/** "question 14", "questions 14 and 34", "questions 3, 14 and 34". */
const questionList = (ns: number[]) => {
    const qs = ns.map(String);
    return qs.length > 1 ? `questions ${qs.slice(0, -1).join(", ")} and ${qs[qs.length - 1]}` : `question ${qs[0]}`;
};

/**
 * What a word edit did. A service names the questions it now sits in
 * ("Pilates rehab now in questions 14 and 34"); any other word says its new value.
 */
const wordEffectLabel = (a: Assembly, variable: string, value: string) => {
    if (!value) return `${wordLabel(variable)} cleared`;
    const next = normalise(value);
    if (wordFamily(variable) === "service") {
        const ns = a.rows.filter((r) => !r.question.locked && r.question.fill_vars.includes(variable)).map((r) => r.question.n);
        if (ns.length > 0 && ns.length <= 3) return `${next.charAt(0).toUpperCase()}${next.slice(1)} now in ${questionList(ns)}`;
        if (ns.length > 3) return `${next.charAt(0).toUpperCase()}${next.slice(1)} now in ${ns.length} questions`;
    }
    // A price nobody published: say it is staff's figure, never a found one.
    if (variable === "price_point") return `Price set to ${next} (your choice)`;
    return `Now “${next}” for ${wordLabel(variable).toLowerCase()}`;
};

export function patchWord(id: string, version: number, variable: string, value: string): ScanDraftResult<ScanDraftWrite> {
    const state = loadForWrite(id, version);
    if ("ok" in state) return state;
    const a = assemble(state);
    return commit(state, wordEffectLabel(a, variable, value), (s) => applyWord(s, a, variable, value));
}

/* ------------------------------------------------------------------------- *
 * Decisions
 * ------------------------------------------------------------------------- */

function outletChange(s: DraftState, action: Extract<DecisionAction, { kind: "confirm_outlet" | "drop_outlet" | "add_outlet" | "set_town" }>): ScanDraftFailure | null {
    const same = (name: string) => (o: { name: string }) => o.name.toLowerCase() === name.toLowerCase();
    if (action.kind === "set_town") s.business.outlets = s.business.outlets.map((o) => (same(action.outlet)(o) ? { ...o, town: action.town } : o));
    if (action.kind === "confirm_outlet") s.business.outlets = s.business.outlets.map((o) => (same(action.name)(o) ? { ...o, confirmed: true } : o));
    if (action.kind === "drop_outlet") s.business.outlets = s.business.outlets.filter((o) => !same(action.name)(o));
    if (action.kind === "add_outlet" && !s.business.outlets.some(same(action.name)))
        s.business.outlets = [...s.business.outlets, { name: action.name, town: action.town, source: action.source ?? "staff", confirmed: true }];
    return null;
}

const BUY_LABEL: Record<ScanDraftBusiness["buy"], string> = { visit: "Visit us", online: "Online", both: "Both", we_go_to_them: "We go to them" };

/**
 * `set_buy` (mode_mismatch): change how customers buy and draft again in one
 * write. To a mode with a door, the showrooms staff added count as confirmed
 * (the answer says they are open); to Online, the brand fields come from the
 * site read when the form has none.
 */
function applySetBuy(s: DraftState, buy: ScanDraftBusiness["buy"]): ScanDraftFailure | null {
    const read = s.sampleDomain ? sampleFor(s.sampleDomain)?.detected : null;
    const draft: ScanDraftBusiness = { ...clone(s.business), buy };
    if (buy !== "online") draft.outlets = draft.outlets.map((o) => (o.source === "staff" ? { ...o, confirmed: true } : o));
    if (buy === "online") {
        // Online has no outlets (the payload sends none): a door would raise this card again.
        draft.outlets = [];
        if (!draft.markets?.length && read?.marketsGuess?.length) draft.markets = clone(read.marketsGuess);
        if (draft.sellsToBusinesses === undefined && typeof read?.sellsToBusinessesGuess === "boolean") draft.sellsToBusinesses = read.sellsToBusinessesGuess;
        if (draft.partnerProducts === undefined && read?.partnerProductsGuess) draft.partnerProducts = [...read.partnerProductsGuess];
    }
    const next = cleanBusiness(draft);
    if (isFailure(next)) return next;
    const townAnchor = anchorIsTownRefusal(next);
    if (townAnchor) return townAnchor;
    s.business = next;
    redraft(s);
    startJob(s, STEP_MS_REDRAFT, false);
    return null;
}

function actionLabel(action: DecisionAction, n: number | null, decisionTitle: string, a?: Assembly): string {
    switch (action.kind) {
        case "edit_word":
            return a ? wordEffectLabel(a, action.variable, action.value) : `${wordLabel(action.variable)} changed`;
        case "swap_question":
            return `Question ${action.n} asked another way`;
        case "reword":
            return `Question ${action.n} reworded`;
        case "confirm_outlet":
            return `${action.name} kept. Draft again to give it questions`;
        case "drop_outlet":
            return `${action.name} left out. Draft again to share its questions`;
        case "add_outlet":
            return `${action.name} added. Draft again to give it questions`;
        case "set_town":
            return `City-wide questions for ${action.outlet} now ask ${action.town}`;
        case "rebuild":
            return "Drafted the 50 again";
        case "set_buy":
            return `Switched to ${BUY_LABEL[action.buy]} and drafted the 50 again`;
        case "refresh_words":
            return "Drafted the words again";
        case "recipe":
            return action.mode === "take_all" ? "Took the new recipe" : "Kept the old wording";
        case "ack":
            return "Trend break noted";
        case "dismiss":
            if (n !== null) return `Question ${n} left as it is`;
            if (decisionTitle.endsWith(" is in no question")) return `${decisionTitle.replace(/ is in no question$/, "")} left out`;
            if (decisionTitle.endsWith(" is not in any question")) return `${decisionTitle.replace(/ is not in any question$/, "")} left out`;
            if (decisionTitle.startsWith("People search")) return "Search ignored";
            {
                // Name what was left, so "Last:" says it (JOURNEY-SPEC §2.5 form cards, duplicates).
                const service = /^.+? lists (.+), not in the 50$/.exec(decisionTitle);
                if (service) return `${service[1].charAt(0).toUpperCase()}${service[1].slice(1)} left out`;
                const outlet = /^.+? names an outlet in (.+)$/.exec(decisionTitle);
                if (outlet) return `${outlet[1]} is not an outlet`;
                const pair = /^Questions (\d+) and (\d+) ask the same thing$/.exec(decisionTitle);
                if (pair) return `Questions ${pair[1]} and ${pair[2]} both kept`;
                return `Left as it is: ${decisionTitle}`;
            }
        default:
            return "Done";
    }
}

/** Run a write as the answer to one decision: commit() counts it even when the draft rebuilds or closes. */
function asAnswer(decisionId: string, write: () => ScanDraftResult<ScanDraftWrite>): ScanDraftResult<ScanDraftWrite> {
    answering = decisionId;
    try {
        return write();
    } finally {
        answering = null;
    }
}

export function answerDecision(id: string, version: number, decisionId: string, choice: DecisionChoice): ScanDraftResult<ScanDraftWrite> {
    return asAnswer(decisionId, () => applyDecision(id, version, decisionId, choice));
}

function applyDecision(id: string, version: number, decisionId: string, choice: DecisionChoice): ScanDraftResult<ScanDraftWrite> {
    const state = loadForWrite(id, version);
    if ("ok" in state) return state;
    const d = view(state).decisions.find((x) => x.id === decisionId);
    if (!d) return fail("refused", "That decision no longer applies. The list has been refreshed.", { code: "decision_gone" });
    if (d.state !== "open") return fail("refused", "That one is already answered.", { code: "decision_answered" });

    if (choice === "dismiss" || choice === "ack") {
        if (d.severity === "blocker") return fail("refused", "This one has to be fixed before Approve.", { code: "blocker" });
        const label = choice === "ack" ? "Trend break noted" : actionLabel({ kind: "dismiss" }, null, d.title);
        return commit(state, label, (s) => {
            s.answered[d.id] = choice === "ack" ? "acked" : "dismissed";
            return null;
        });
    }
    const option = choice === "recommended" ? d.recommended : d.alternatives[choice.alternative] ?? null;
    if (!option) return fail("refused", "That option is not on this decision.", { code: "option_missing" });
    const action = option.action;
    if (CLIENT_SIDE_ACTIONS.includes(action.kind)) return fail("refused", "That option is handled on the screen.", { code: "client_side" });
    if (action.kind === "recipe") return applyRecipe(id, version, action.mode);
    if (action.kind === "rebuild" || action.kind === "refresh_words") return rebuild(id, version, { refreshWords: action.kind === "refresh_words" });

    const a = assemble(state);
    const label = actionLabel(action, null, d.title, a);
    const fromForm = d.type.startsWith("form_") || d.type === "outlets_vs_plan";
    switch (action.kind) {
        case "add_service":
            return commit(state, firstCheckLabel(action, a, state), (s) => applyAddService(s, a, action.name));
        case "drop_service":
            return commit(state, `${action.name} left out`, (s) => applyDropService(s, a, action.name));
        case "record":
            return commit(state, recordLabel(d.type), (s) => {
                s.answered[d.id] = "resolved";
                return null;
            });
        case "set_plan_keywords":
            return commit(state, `${plural(action.keywords.length, "priority keyword")} for the plan`, (s) => applyPlanKeywords(s, a, action.keywords), { lintGate: false });
        case "set_plan_outlets":
            return commit(state, `${plural(action.names.length, "outlet name")} for the plan`, (s) => applyPlanOutlets(s, action.names), { lintGate: false });
        case "use_pitch_run":
            // Recorded on the draft: Start then makes the running pitch the first AI check (H4-e).
            return commit(state, "The pitch scan becomes the first AI check", (s) => {
                s.answered[d.id] = "resolved";
                return null;
            });
        case "set_core_service":
            return commit(state, `${action.name.charAt(0).toUpperCase()}${action.name.slice(1)} is the core service`, (s) => applyCoreService(s, a, action.name));

        case "add_outlet":
        case "drop_outlet":
            if (fromForm) {
                // A form card changes only that outlet's rows, at once: no Draft again.
                return commit(
                    state,
                    action.kind === "add_outlet" ? `${action.name} added with its own questions` : `${action.name} left out. Its questions went to the other outlets`,
                    (s) => {
                        outletChange(s, action);
                        if (action.kind === "add_outlet") {
                            s.business.outlets = s.business.outlets.map((o) => (o.name.toLowerCase() === action.name.toLowerCase() ? { ...o, source: "form" } : o));
                        }
                        const refused = anchorIsTownRefusal(s.business);
                        if (refused) return refused;
                        redraft(s);
                        return null;
                    },
                    { lintGate: false },
                );
            }
            return commit(state, label, (s) => outletChange(s, action), { lintGate: false });
        case "edit_word":
            return commit(state, label, (s) => applyWord(s, a, action.variable, action.value));
        case "swap_question":
            return commit(state, label, (s) => applyAlternative(s, a, action.n, action.altId));
        case "reword":
            return commit(state, label, (s) => applyReword(s, a, action.n, action.text));
        case "confirm_outlet":
            return commit(state, label, (s) => outletChange(s, action), { lintGate: false });
        case "set_buy":
            return commit(state, label, (s) => applySetBuy(s, action.buy), { lintGate: false });
        case "set_town":
            // The town and the no-AI rebuild in one write, one undo step: the 50 are re-rendered
            // with the city rows in the wider town and the dedupe pass re-runs, so the decision
            // closes in one click. The draft stays ready (no job, no spend).
            return commit(
                state,
                label,
                (s) => {
                    outletChange(s, action);
                    redraft(s);
                    return null;
                },
                { lintGate: false },
            );
        case "ack":
        case "dismiss":
            if (d.severity === "blocker") return fail("refused", "This one has to be fixed before Approve.", { code: "blocker" });
            return commit(state, label, (s) => {
                s.answered[d.id] = action.kind === "ack" ? "acked" : "dismissed";
                return null;
            });
        default:
            return fail("refused", "That option is handled on the screen.", { code: "client_side" });
    }
}

/**
 * A "For the plan" card answered with an edited list (H5-a: the live route
 * takes `{choice: "recommended", values}`). On an open card it counts as the
 * answer; on an answered card only the list changes. One undo step either way.
 */
export function answerPlanList(id: string, version: number, decisionId: string, values: string[]): ScanDraftResult<ScanDraftWrite> {
    const state = loadForWrite(id, version);
    if ("ok" in state) return state;
    const d = view(state).decisions.find((x) => x.id === decisionId);
    if (!d || (d.type !== "plan_keywords" && d.type !== "plan_outlet_names"))
        return fail("refused", "That decision no longer applies. The list has been refreshed.", { code: "decision_gone" });
    const keywords = d.type === "plan_keywords";
    if (keywords) {
        // Keywords go to the save action as typed: a blank or a repeat is refused, never dropped quietly.
        const a = assemble(state);
        const label = `${plural(values.length, "priority keyword")} for the plan`;
        const write = () => commit(state, label, (s) => applyPlanKeywords(s, a, values), { lintGate: false });
        return d.state === "open" ? asAnswer(decisionId, write) : write();
    }
    // Outlet names too go to the save action as typed: a blank, a repeat or a missing outlet is refused.
    const label = `${plural(values.length, "outlet name")} for the plan`;
    const write = () => commit(state, label, (s) => applyPlanOutlets(s, values), { lintGate: false });
    return d.state === "open" ? asAnswer(decisionId, write) : write();
}

/* ------------------------------------------------------------------------- *
 * Recipe change
 * ------------------------------------------------------------------------- */

/** The recipe change is a decision too: every mode counts as one answered. */
export function setRecipe(id: string, version: number, mode: RecipeMode, picks?: Record<number, "take" | "keep">): ScanDraftResult<ScanDraftWrite> {
    return asAnswer("recipe_change:recipe", () => applyRecipe(id, version, mode, picks));
}

function applyRecipe(id: string, version: number, mode: RecipeMode, picks?: Record<number, "take" | "keep">): ScanDraftResult<ScanDraftWrite> {
    const state = loadForWrite(id, version);
    if ("ok" in state) return state;
    if (state.kind !== "recipe_change") return fail("refused", "This draft has no recipe change.", { code: "no_recipe_change" });
    const a = assemble(state);
    const changedKeys = a.rows.filter((r) => (state.baseline?.[r.plan.key] ?? r.proposed) !== r.proposed).map((r) => r.plan.key);
    const oldTemplateKeys = Object.keys(a.sample?.existing?.oldTemplates ?? {});
    const keep = new Set(
        mode === "choose_each"
            ? a.rows
                  .filter((r) => changedKeys.includes(r.plan.key) && r.plan.key !== FORCED_SLOT_KEY && picks?.[r.question.n] === "keep")
                  .map((r) => r.plan.key)
            : [],
    );
    const label =
        mode === "take_all"
            ? `Took the new recipe for all ${changedKeys.length}`
            : mode === "keep_all"
              ? "Kept the current 50. No scan started."
              : `${changedKeys.length - keep.size} take · ${keep.size} keep`;
    return commit(state, label, (s) => {
        s.recipeMode = mode;
        if (mode === "take_all") s.oldKeys = [];
        if (mode === "keep_all") {
            s.oldKeys = oldTemplateKeys;
            // Keep all leaves the current 50, so the draft closes, as the backend does.
            s.status = "discarded";
        }
        if (mode === "choose_each") {
            s.oldKeys = oldTemplateKeys.filter((k) => keep.has(k));
            // A kept row keeps its stored wording from here on: it is locked.
            const after = assemble(s);
            keep.forEach((key) => {
                s.rows[key] = { ...(s.rows[key] ?? {}), locked: true, frozen: frozenOf(after, key) };
            });
        }
        return null;
    });
}


/* ------------------------------------------------------------------------- *
 * First AI check (joined journey, JOURNEY-SPEC §2.5, §5; H2-a, H3-a, H4-a)
 * ------------------------------------------------------------------------- */

/** Recipe stamps whose sets a later scan may copy (BE reusable_stamps). Brand and online join when they go live (H8-a). */
const REUSABLE_STAMPS = new Set<string>([RECIPES.visit.stamp]);

const firstChecksOf = (businessId: string) =>
    Array.from(drafts.values())
        .filter((d) => d.purpose === "first_check" && d.businessId === businessId)
        .sort((x, y) => y.updatedAt - x.updatedAt);

/** The newest started prospect draft on a site: the platform pitch made this session. */
function startedPitch(domain: string): DraftState | null {
    return (
        Array.from(drafts.values())
            .filter((d) => d.kind === "new_scan" && !d.purpose && d.domain === domain && d.status === "started")
            .sort((x, y) => y.updatedAt - x.updatedAt)[0] ?? null
    );
}

/**
 * What a pitch started in this session tells the Client signed sheet (the
 * mock of GET /onboarding/prefill): its outlets, buy mode and day. Null when
 * no prospect draft for this site has started here.
 */
export function startedPitchPrefill(domain: string): { outletAreas: string[]; buyMode: BuyMode; pitchedAt: string; services: string[]; category: string } | null {
    const started = startedPitch(domainKey(domain));
    if (!started) return null;
    return {
        services: started.business.services.map((x) => x.name.trim()).filter(Boolean),
        category: started.business.category,
        outletAreas: started.business.outlets.map((o) => o.name.trim()).filter(Boolean),
        buyMode: started.business.buy,
        pitchedAt: new Date(started.updatedAt).toISOString(),
    };
}

/**
 * The name a prospect scanned in this session holds. Live, POST /assessments
 * creates the business at the start, so signing it keeps its own name rather
 * than the quote's. Null when no prospect scan for this site started here.
 */
export function startedPitchName(domain: string): string | null {
    return startedPitch(domainKey(domain))?.business.businessName || null;
}

/** A draft the sequence never sees: the sample pitch, assembled only to copy its 50. */
function pitchState(domain: string, business: ScanDraftBusiness, at: number): DraftState {
    return {
        kind: "new_scan",
        domain,
        businessId: null,
        sourceAssessmentId: null,
        sampleDomain: domain,
        recipeFrom: RECIPES[business.buy],
        id: `pitch-${slug(domain)}`,
        status: "started",
        version: 1,
        business: clone(business),
        built: clone(business),
        round: 0,
        wordEdits: {},
        rows: {},
        answered: {},
        oldKeys: [],
        recipeMode: null,
        job: null,
        answeredCount: 0,
        baseline: null,
        createdAt: at,
        updatedAt: at,
        approvedAt: at,
        startedAssessmentId: null,
        note: null,
        undo: [],
    };
}

/** The pitch a first_check draft starts from: this session's started prospect draft, else the sample's. */
function pitchFor(domain: string): { state: DraftState; pitch: ScanDraftPitch; fromDraftId: string | null } | null {
    const started = startedPitch(domain);
    if (started) {
        const stamp = RECIPES[started.built.buy].stamp;
        return {
            state: started,
            pitch: {
                assessmentId: started.startedAssessmentId ?? `assessment-${started.id}`,
                scannedAt: new Date(started.updatedAt).toISOString(),
                by: started.business.requestedBy,
                stamp,
                reusable: REUSABLE_STAMPS.has(stamp),
            },
            fromDraftId: started.id,
        };
    }
    const sample = sampleFor(domain);
    const meta = journeySample(domain);
    if (!sample?.pitch || !meta?.pitch) return null;
    const at = daysAgoIso(meta.pitch.daysAgo);
    const state = pitchState(domain, sample.pitch.business, new Date(at).getTime());
    dedupePass(state);
    return {
        state,
        pitch: { assessmentId: meta.pitch.assessmentId, scannedAt: at, by: meta.pitch.by, stamp: sample.pitch.stamp, reusable: REUSABLE_STAMPS.has(sample.pitch.stamp) },
        fromDraftId: null,
    };
}

/**
 * H3-a: the first_check draft for a signed client, from the pitched set (copied,
 * no AI, when its stamp is reusable), the form and the quote. Created approved
 * with the pitch's approval when nothing is left to decide; else it builds
 * for a moment, as the background job would.
 */
function buildFirstCheck(facts: SignedFacts, upgrade: DraftState | null): DraftState {
    const domain = domainKey(facts.domain);
    const sample = sampleFor(domain);
    const source = pitchFor(domain);
    const pitch = source?.pitch ?? null;
    const copied = pitch?.reusable ? source?.state ?? null : null;
    const read = sample?.detected ?? unknownRead();
    const business = firstCheckBusiness({
        pitch: copied ? copied.built : null,
        read,
        facts: { formServices: facts.form?.services ?? [], formAreas: facts.form?.areas ?? [], quoteAreas: facts.quote?.areas ?? [] },
        businessName: facts.businessName,
        buy: facts.buyMode,
    });
    const state =
        upgrade ??
        newState({
            kind: "new_scan",
            domain,
            businessId: facts.businessId,
            sourceAssessmentId: pitch?.assessmentId ?? null,
            sampleDomain: sample ? sample.domain : null,
            recipeFrom: RECIPES[business.buy],
            business,
        });
    state.purpose = "first_check";
    state.businessId = facts.businessId;
    state.sourceAssessmentId = pitch?.assessmentId ?? state.sourceAssessmentId;
    if (!upgrade) {
        if (copied) {
            state.wordEdits = clone(copied.wordEdits);
            state.rows = clone(copied.rows);
            state.round = copied.round;
            state.auto = clone(copied.auto ?? {});
            // What the BD already decided at the pitch ("Keep both" on two near-twins) stays decided (H3-a).
            state.answered = clone(copied.answered);
        } else {
            dedupePass(state);
        }
    }
    const allOutlets = read.outlets.map((o) => o.name);
    const quoteAreas = (facts.quote?.areas ?? []).filter((x) => x.trim() && !canonicalTown(x));
    state.firstCheck = {
        contactName: facts.contactName,
        answeredAt: facts.answeredAt,
        formServices: [...(facts.form?.services ?? [])],
        formAreas: [...(facts.form?.areas ?? [])],
        customerQuestions: [...(facts.form?.customerQuestions ?? [])],
        goals: [...(facts.form?.goals ?? [])],
        plans: facts.form?.plans ?? "",
        other: facts.form?.other ?? "",
        quote: facts.quote ? { ref: facts.quote.ref, outlets: facts.quote.outlets } : null,
        // Plan outlet names list every outlet sold, a chain's beyond the 9 included.
        planOutlets: allOutlets.length > OUTLET_CAP ? allOutlets : quoteAreas,
        heldKeywords: [...facts.heldKeywords],
        heldOutletNames: [...facts.heldOutletNames],
        pitch,
        pitchServices: copied ? copied.built.services.map((x) => x.name) : [],
        pitchOutlets: copied ? copied.built.outlets.map((x) => x.name) : [],
        siteNames: [...(sample?.siteNames ?? [])],
        pitchTexts: copied ? Object.fromEntries(assemble(copied).rows.map((r) => [r.plan.key, r.question.text])) : null,
    };
    // Carried approval: the pitched 50 unchanged (an upgraded draft's own edits count) and nothing to decide.
    const now = view(state);
    if (copied && !openGates(now.decisions).length && now.changedVsPitch === 0 && !isStale(state)) {
        state.status = "approved";
        state.approvedAt = clock();
        state.carriedApproval = { by: pitch?.by ?? "the BD", at: pitch?.scannedAt ?? new Date(clock()).toISOString(), fromDraftId: source?.fromDraftId ?? null };
    } else if (!upgrade) {
        startJob(state, STEP_MS_REDRAFT, false);
    }
    state.version += 1;
    state.updatedAt = clock();
    return state;
}

/**
 * The business's First AI check draft, drafted when its form is in and none
 * is open (O1, O7). Nothing for an Online client (it waits for brand checks),
 * nor once the first check runs or ran; a failed first check reopens its
 * started draft as approved, so Run it again asks the same 50 (H4-a). An open prospect draft on the same site is
 * upgraded and keeps its edits (H2-a). Null when there is none.
 */
export function ensureFirstCheck(businessId: string): ScanDraft | null {
    const facts = onboardingSide()?.facts(businessId) ?? null;
    const mine = firstChecksOf(businessId);
    mine.forEach(advance);
    const open = mine.find(isOpen);
    if (open) return view(open);
    if (!facts || !facts.answeredAt || facts.buyMode === "online") return mine[0] ? view(mine[0]) : null;
    const run = facts.firstCheck.status;
    const started = mine.find((d) => d.status === "started");
    if (run === "running" || run === "complete") return started ? view(started) : null;
    if (started && run !== "failed") return view(started);
    if (started) {
        // Run it again (H4-a): the same approved 50 start again, never a fresh draft that drops the review.
        started.status = "approved";
        started.startedAssessmentId = null;
        started.version += 1;
        started.updatedAt = clock();
        return view(started);
    }
    const domain = domainKey(facts.domain);
    const prospect = Array.from(drafts.values()).find((d) => d.kind === "new_scan" && !d.purpose && d.domain === domain && isOpen(d)) ?? null;
    const state = buildFirstCheck(facts, prospect);
    drafts.set(state.id, state);
    return view(state);
}

/** POST /scan-drafts {businessId, purpose: "first_check"} (H2-a). */
function createFirstCheck(businessId: string): ScanDraftResult<{ draft: ScanDraft; reused: boolean }> {
    const facts = onboardingSide()?.facts(businessId) ?? null;
    if (!facts) return fail("refused", "This business has no onboarding record.", { code: "no_onboarding" });
    if (facts.firstCheck.status === "running") return fail("refused", "The first AI check is already running.", { code: "scan_running" });
    if (facts.firstCheck.status === "complete") return fail("refused", "The first AI check already finished.", { code: "scan_finished" });
    if (facts.buyMode === "online") return fail("refused", "The first AI check waits for brand checks.", { code: "brand_not_live" });
    if (!facts.answeredAt) return fail("refused", "The client has not answered the form yet.", { code: "form_not_answered" });
    const before = firstChecksOf(businessId).find(isOpen) ?? null;
    const draft = ensureFirstCheck(businessId);
    if (!draft) return fail("refused", "The first AI check could not be drafted.", { code: "draft_failed" });
    return ok({ draft, reused: before !== null && before.id === draft.id });
}

/** The onboarding record's `scan.draft`: the newest first_check draft for the business, drafted if due. */
export interface FirstCheckDraftSummary {
    id: string;
    status: ScanDraft["status"];
    openDecisions: number;
    approvedBy: string | null;
    approvedAt: string | null;
    purpose: "first_check";
    fromPitch: boolean;
}

export function firstCheckSummary(businessId: string): FirstCheckDraftSummary | null {
    const d = ensureFirstCheck(businessId);
    if (!d) return null;
    return {
        id: d.id,
        status: d.status,
        openDecisions: d.status === "ready" ? openGates(d.decisions).length : 0,
        approvedBy: d.carriedApproval?.by ?? (d.approvedAt ? d.summary.requestedBy : null),
        approvedAt: d.approvedAt,
        purpose: "first_check",
        fromPitch: Boolean(d.firstCheck?.pitch),
    };
}

/** Question n of the business's started first check, as asked. Null when this session ran none. */
export function firstCheckQuestion(businessId: string, n: number): string | null {
    const run = firstChecksOf(businessId).filter((d) => d.status === "started").sort((a, b) => b.updatedAt - a.updatedAt)[0];
    return run ? view(run).questions.find((q) => q.n === n)?.text ?? null : null;
}

/** Sample tools: forget a business's First AI check drafts (its journey rewound before the form). */
export function forgetFirstChecks(businessId: string): void {
    firstChecksOf(businessId).forEach((d) => drafts.delete(d.id));
}

const RECORD_LABEL: Partial<Record<string, string>> = {
    form_service_missing: "Kept as pitched",
    form_outlet_missing: "Kept as pitched",
    outlets_vs_plan: "Outlet count noted",
    outlets_exceed_slots: `These ${OUTLET_CAP} keep their questions`,
};
const recordLabel = (type: string) => RECORD_LABEL[type] ?? "Noted";

function firstCheckLabel(action: Extract<DecisionAction, { kind: "add_service" }>, a: Assembly, s: DraftState): string {
    const target = serviceTarget(a.rows, formServicesOf(s.business));
    return target ? wordEffectLabel(a, target.variable, action.name) : `${action.name} added`;
}

/** "Add it": the service joins the business and takes a service word's rows; the rest stay as they are. */
function applyAddService(s: DraftState, a: Assembly, name: string): ScanDraftFailure | null {
    const target = serviceTarget(a.rows, formServicesOf(s.business));
    if (!target) return fail("refused", "No question has room for another service.", { code: "no_service_slot" });
    if (!s.business.services.some((x) => sameThing(x.name, name))) {
        s.business.services = [...s.business.services, { name, source: "form" }];
        s.built = { ...s.built, services: [...s.built.services, { name, source: "form" }] };
    }
    return applyWord(s, a, target.variable, name);
}

/**
 * "Make it core" (form goals): the service takes the main-service word, and
 * the service word it held (when it held one) takes the old main service, so
 * no service is asked twice and none is lost. The form service joins the
 * business when it was not on it.
 */
function applyCoreService(s: DraftState, a: Assembly, name: string): ScanDraftFailure | null {
    const { twin } = coreSwap(a.rows, a.src, name);
    const old = a.src.value("core_service");
    if (a.lintCtx.nameTokens.some((t) => new RegExp(`\\b${t}\\b`, "i").test(name)))
        return fail("lint", "That word names the business. The questions must not.", { code: "value_names_business" });
    s.wordEdits.core_service = normalise(name);
    if (twin && old) s.wordEdits[twin] = old;
    // The core service can be switched back and forth: a goal card answered before asks again
    // whenever its service stops being core (it is only raised then), instead of staying answered.
    Object.keys(s.answered)
        .filter((id) => id.startsWith("form_goal_core:"))
        .forEach((id) => delete s.answered[id]);
    if (!s.business.services.some((x) => sameThing(x.name, name))) {
        const item = { name: `${name.charAt(0).toUpperCase()}${name.slice(1)}`, source: "form" as const };
        s.business.services = [...s.business.services, item];
        s.built = { ...s.built, services: [...s.built.services, item] };
    }
    return null;
}

/** "Drop it": the service leaves; the word that asked it moves to its next pick. */
function applyDropService(s: DraftState, a: Assembly, name: string): ScanDraftFailure | null {
    s.business.services = s.business.services.filter((x) => !sameThing(x.name, name));
    s.built = { ...s.built, services: s.built.services.filter((x) => !sameThing(x.name, name)) };
    const word = a.rows.flatMap((r) => r.question.fill_vars).find((v) => sameThing(a.src.value(v), name));
    if (!word) return null;
    const next = a.src.alternatives(word)[0];
    return next ? applyWord(s, a, word, next) : null;
}

/** Saves the plan's priority keywords only when the approval gate would accept them (validPlanKeywords). */
function applyPlanKeywords(s: DraftState, a: Assembly, keywords: string[]): ScanDraftFailure | null {
    const check = validPlanKeywords(keywords, { businessName: s.business.businessName, nameTokens: a.lintCtx.nameTokens });
    if (!check.ok) return fail("refused", check.message, { code: check.code });
    s.forPlan = { priorityKeywords: check.keywords, outletNames: s.forPlan?.outletNames ?? null };
    return null;
}

/** Saves the plan's outlet names only when the approval gate would accept them (validPlanOutletNames). */
function applyPlanOutlets(s: DraftState, names: string[]): ScanDraftFailure | null {
    const required = s.firstCheck ? planOutletNames(s.business, s.firstCheck) : [];
    if (required.length < 2) return fail("refused", "This draft has no outlet names to set for the plan.", { code: "outlet_names_not_asked" });
    const check = validPlanOutletNames(names, required);
    if (!check.ok) return fail("refused", check.message, { code: check.code });
    s.forPlan = { priorityKeywords: s.forPlan?.priorityKeywords ?? null, outletNames: check.names };
    return null;
}

/* ------------------------------------------------------------------------- *
 * Undo, approve, reopen, start, discard
 * ------------------------------------------------------------------------- */

export function undo(id: string, version: number): ScanDraftResult<ScanDraftWrite> {
    const state = loadForWrite(id, version);
    if ("ok" in state) return state;
    const last = state.undo.pop();
    if (!last) return fail("refused", "Nothing to undo.", { code: "nothing_to_undo" });
    const beforeA = assemble(state);
    restore(state, last.content);
    advance(state);
    state.version += 1;
    state.updatedAt = clock();
    const afterA = state.status === "building" ? beforeA : assemble(state);
    return ok({ draft: view(state), effect: { ...effectOf(`Undone: ${last.label}`, beforeA, afterA) } });
}

export function approve(id: string, version: number): ScanDraftResult<ScanDraftWrite> {
    const state = loadForWrite(id, version, ["ready", "approved"]);
    if ("ok" in state) return state;
    if (state.status === "approved") return ok({ draft: view(state), effect: null });
    if (isStale(state)) return fail("refused", "The business changed since the 50 were drafted. Draft again first.", { code: "questions_stale" });
    const gates = openGates(view(state).decisions);
    if (gates.length) return fail("refused", `Answer the ${plural(gates.length, "decision")} first.`, { code: "decisions_open" });
    state.status = "approved";
    state.approvedAt = clock();
    state.version += 1;
    state.updatedAt = clock();
    return ok({ draft: view(state), effect: { label: "Approved", changed: [], skipped: [] } });
}

export function reopen(id: string, version: number): ScanDraftResult<ScanDraftWrite> {
    const state = loadForWrite(id, version, ["approved", "ready"]);
    if ("ok" in state) return state;
    if (state.status === "approved") {
        state.status = "ready";
        state.approvedAt = null;
        state.version += 1;
        state.updatedAt = clock();
    }
    return ok({ draft: view(state), effect: { label: "Reopened", changed: [], skipped: [] } });
}

export function start(id: string, version: number, req: StartScanDraftRequest): ScanDraftResult<StartScanDraftResult> {
    const existing = load(id);
    if ("ok" in existing) return existing;
    const result = (reused: boolean, extra: Partial<StartScanDraftResult> = {}): StartScanDraftResult => ({
        assessmentId: existing.startedAssessmentId ?? "",
        draftId: existing.id,
        businessId: existing.businessId,
        estimatedMinutes: 40, // FIRST_CHECK_MINUTES (onboarding/derive), measured
        reused,
        ...extra,
    });
    if (existing.startedAssessmentId) return ok(result(true));
    const state = loadForWrite(id, version, ["approved"]);
    if ("ok" in state) {
        if (state.code === "draft_ready") return fail("refused", "Approve the 50 before starting the scan.", { code: "draft_not_approved" });
        return state;
    }
    // H4-a / H4-b: the first check's guards, before anything is spent.
    const facts = state.purpose === "first_check" && state.businessId ? onboardingSide()?.facts(state.businessId) ?? null : null;
    if (facts?.firstCheck.status === "running") return fail("refused", "The first AI check is already running.", { code: "scan_running" });
    if (facts?.firstCheck.status === "complete") return fail("refused", "The first AI check already finished.", { code: "scan_finished" });
    if (facts?.buyMode === "online") return fail("refused", "The first AI check waits for brand checks.", { code: "brand_not_live" });
    // Moving the baseline (call 12): only in its first 30 days, and always with a reason.
    if (req.baseline === "move") {
        if (!state.baselineAt) return fail("refused", "This business has no baseline to move.", { code: "no_baseline" });
        if (clock() > state.baselineAt + BASELINE_MOVE_DAYS * 86_400_000)
            return fail("refused", `The baseline moves only in its first ${BASELINE_MOVE_DAYS} days. Start it as a new set.`, { code: "baseline_locked" });
        if (!req.baselineReason?.trim()) return fail("refused", "Say why the baseline moves. It shows in Question history.", { code: "baseline_reason" });
    }
    // H2-c: "At the next monthly check" holds the set for that check and spends nothing.
    const later = req.applyAt === "next_monthly";
    if (later) {
        if (state.kind === "new_scan") return fail("refused", "A new business has no monthly check. Start the scan now.", { code: "monthly_off" });
        if (!state.monthlyAt) return fail("refused", "Monthly checks are off for this business, so the new set needs a rescan now.", { code: "monthly_off" });
        if (req.baseline === "move") return fail("refused", "A baseline moves only with a rescan now.", { code: "baseline_needs_rescan" });
    }
    // H4-e: "Use the pitch scan" on these same 50, still running: the first check is that run.
    const pitch =
        state.purpose === "first_check" && state.answered["pitch_running:pitch"] === "resolved" && view(state).changedVsPitch === 0
            ? startedPitch(domainKey(state.domain))
            : null;
    state.note = req.baseline === "move" && req.baselineReason ? [req.note, `Baseline moved: ${req.baselineReason.trim()}`].filter(Boolean).join(" ") : req.note;
    state.business = { ...state.business, requestedBy: req.requestedBy };
    state.built = { ...state.built, requestedBy: req.requestedBy };
    state.startedAssessmentId = later ? null : pitch ? pitch.startedAssessmentId ?? `assessment-${pitch.id}` : `assessment-${state.id}`;
    state.scheduledFor = later ? state.monthlyAt ?? null : null;
    state.status = "started";
    state.version += 1;
    state.updatedAt = clock();
    // The first AI check: the onboarding record's first assessment, its approved outlets and the plan's answers.
    if (state.purpose === "first_check" && state.businessId && state.startedAssessmentId) {
        onboardingSide()?.firstCheckStarted(state.businessId, {
            assessmentId: state.startedAssessmentId,
            outletAreas: activeOutlets(state.business)
                .map((o) => o.name.trim())
                .filter((n) => n && n.toLowerCase() !== "malaysia"),
            priorityKeywords: state.forPlan?.priorityKeywords ?? null,
            outletNames: state.forPlan?.outletNames ?? null,
            ...(pitch ? { startedAt: new Date(pitch.updatedAt).toISOString() } : {}),
        });
    }
    if (later) return ok(result(false, { scheduledFor: state.scheduledFor ? new Date(state.scheduledFor).toISOString() : null }));
    return ok(result(false, pitch ? { tookOverPitch: true } : {}));
}

/** The next monthly check after now: the baseline's day of the month, from next month on (ms). */
function nextMonthly(baselineAt: number): number {
    const base = new Date(baselineAt);
    const now = new Date(clock());
    const next = new Date(now.getFullYear(), now.getMonth(), base.getDate(), 9, 0, 0, 0);
    while (next.getTime() <= now.getTime()) next.setMonth(next.getMonth() + 1);
    return next.getTime();
}

/* ------------------------------------------------------------------------- *
 * The sample clock: one for every scan (journey.ts SAMPLE_SCAN_MS)
 * ------------------------------------------------------------------------- */

/** A draft whose scan is with the engines: started now, not waiting for a monthly check, not finished. */
const running = (d: DraftState): boolean => d.status === "started" && !d.scheduledFor && !d.finishedAt;

/** Tells the onboarding sample store a pitch finished, so its business row reads the scored scan. */
function pitchDone(d: DraftState): void {
    if (d.kind !== "new_scan" || d.purpose || !d.finishedAt) return;
    onboardingSide()?.pitchFinished({
        domain: d.domain,
        businessName: d.business.businessName || d.domain,
        category: d.business.category || "",
        city: activeOutlets(d.business)[0]?.name ?? d.business.city ?? "",
        assessmentId: d.startedAssessmentId ?? `assessment-${d.id}`,
        startedAt: new Date(d.updatedAt).toISOString(),
        finishedAt: new Date(d.finishedAt).toISOString(),
    });
}

/**
 * Brings every started scan up to the sample clock. A started draft takes no
 * writes, so `updatedAt` is when it started. A First AI check is the
 * onboarding record's run: it finishes when the record says so, and a pitch
 * it took over ("Use the pitch scan") finishes with it.
 */
function settleRuns(): void {
    const now = clock();
    drafts.forEach((d) => {
        if (!running(d)) return;
        if (d.purpose === "first_check" && d.businessId) {
            const run = onboardingSide()?.facts(d.businessId)?.firstCheck;
            if (run && (run.status === "complete" || run.status === "failed") && run.assessmentId === d.startedAssessmentId) {
                d.finishedAt = run.finishedAt ? Date.parse(run.finishedAt) : now;
            }
            return;
        }
        if (now < d.updatedAt + SAMPLE_SCAN_MS) return;
        d.finishedAt = d.updatedAt + SAMPLE_SCAN_MS;
        pitchDone(d);
    });
}

/**
 * Sample tools ("AI check finishes"): every scan on this site that started
 * at or before `upTo` finishes now, so a later scan never finishes before an
 * earlier one. Its pitch row is written as for any finished pitch.
 */
export function finishRunsFor(domain: string, upTo: number): void {
    const key = domainKey(domain);
    const now = clock();
    drafts.forEach((d) => {
        if (!running(d) || domainKey(d.domain) !== key || d.updatedAt > upTo) return;
        d.finishedAt = Math.max(d.updatedAt, Math.min(now, d.updatedAt + SAMPLE_SCAN_MS));
        pitchDone(d);
    });
}

/**
 * The pitch scan still running on the 50 a first_check draft copied: the
 * "Use the pitch scan" card asks about it. Null once it finished.
 */
function runningPitch(state: DraftState): DraftState | null {
    if (state.purpose !== "first_check" || !state.firstCheck?.pitch) return null;
    settleRuns();
    const pitch = startedPitch(domainKey(state.domain));
    return pitch && running(pitch) && (pitch.startedAssessmentId ?? `assessment-${pitch.id}`) === state.firstCheck.pitch.assessmentId ? pitch : null;
}

/** Drafts whose scan is running now, newest first. */
export function listStarted(): StartedScanDraft[] {
    settleRuns();
    return Array.from(drafts.values())
        .filter(running)
        // A first check stops reading as scanning once the onboarding record's baseline is in.
        .filter((d) => !(d.purpose === "first_check" && d.businessId && onboardingSide()?.facts(d.businessId)?.firstCheck.status === "complete"))
        .sort((a, b) => b.updatedAt - a.updatedAt)
        .map((d) => ({
            draftId: d.id,
            kind: d.kind,
            ...(d.purpose ? { purpose: d.purpose } : {}),
            domain: d.domain,
            businessId: d.businessId,
            businessName: d.business.businessName || d.domain,
            startedAt: new Date(d.updatedAt).toISOString(),
            estimatedMinutes: 40, // FIRST_CHECK_MINUTES (onboarding/derive), measured
        }));
}

export function discard(id: string, version?: number): ScanDraftResult<null> {
    const state = load(id);
    if ("ok" in state) return state;
    if (typeof version === "number" && state.version !== version)
        return fail("stale", "Someone else changed this draft. Showing the latest.", { code: "stale_draft" });
    if (state.status === "started") return fail("refused", "This draft already started a scan.", { code: "draft_started" });
    state.status = "discarded";
    state.version += 1;
    state.updatedAt = clock();
    return ok(null);
}

/**
 * "Pull a year of searches": starts the pull and answers at once
 * with `search.pulling` true; the version does not move. A second press while
 * it runs starts nothing new.
 */
export function refreshSearch(id: string): ScanDraftResult<ScanDraftWrite> {
    const state = load(id);
    if ("ok" in state) return state;
    const key = domainKey(state.domain);
    const g = gsc.get(key);
    if (!g || clock() < g.connectedAt) return fail("refused", "Connect Search Console first.", { code: "gsc_not_connected" });
    if (clock() < (g.pullingUntil ?? 0)) return ok({ draft: view(state), effect: null });
    g.pullingUntil = clock() + GSC_PULL_MS;
    return ok({ draft: view(state), effect: { label: "Pulling a year of Search Console searches", changed: [], skipped: [] } });
}

/* ------------------------------------------------------------------------- *
 * Search Console
 * ------------------------------------------------------------------------- */

export function searchConsoleState(domain: string): { status: "connected" | "not_connected" | "action_needed"; property: string | null } {
    const c = connection(domain);
    return { status: c.connected ? "connected" : "not_connected", property: c.property };
}

/** Starts a connect: it reads as connected about 1.2 seconds later. */
export function connectSearchConsole(domain: string): { redirectUrl: string | null } {
    const key = domainKey(domain);
    if (!gsc.has(key)) gsc.set(key, { connectedAt: clock() + GSC_CONNECT_MS });
    return { redirectUrl: null };
}
